import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import {
  ImageContentFragment,
  ImageFragment,
  PieceFragment,
  RichTextFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import ButtonLink from '@/ui/components/shared/ButtonLink';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { VideoSource, resolveVideoSource } from '@/utils/video-source';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { isDirectMediaUrl } from '@/utils/video-url-helper';
import { VideoHeroBackground } from './video-hero-background';
import { getContentPosition, getOverlayClasses } from './video-hero-styles';
import { VideoHeroTile } from './video-hero-types';

// Wrapper, frame and content layout deliberately mirror the ImageWithContent tile
// (`src/ui/components/tiles/ImageWithContent`), whose place the hero takes once a video is
// configured. Keep the two in step until the layout is extracted into a shared module.

type TileFlag = boolean | null | undefined;

/** A hero on top of the page slides under the transparent header, the other variants do not. */
function getWrapperClasses(isFullWidth: TileFlag, isNotOnTop: TileFlag): string {
  if (!isFullWidth) {
    return 'mx-auto w-full max-w-screen-2xl px-4 sm:px-8';
  }

  if (!isNotOnTop) {
    return '-mt-[104px] pb-20';
  }

  return 'my-8 sm:my-14 lg:my-16';
}

function getFrameClasses(isFullWidth: TileFlag, contentClasses: string): string {
  return twMerge(
    'relative flex p-5 sm:p-8',
    isFullWidth ? 'h-screen' : 'h-sm sm:h-md lg:h-lg',
    contentClasses,
    isFullWidth ? 'mt-0' : 'my-6 overflow-hidden rounded-xl sm:my-10',
  );
}

interface HeroBackdropProps {
  /** The editorial poster, or the still generated for the upload when the editor set none. */
  posterImage?: ImageFragment;
  videoSource?: VideoSource;
  videoSeo?: PieceFragment | null;
  shouldLoop: boolean;
  gradientClass?: string;
  /** Corner the video's pause/play button sits in: the bottom-left of the hero frame. */
  playbackButtonClasses: string;
}

/** The three stacked layers behind the text: poster image, optional video, optional overlay. */
function HeroBackdrop({
  posterImage,
  videoSource,
  videoSeo,
  shouldLoop,
  gradientClass,
  playbackButtonClasses,
}: Readonly<HeroBackdropProps>) {
  return (
    <>
      <OptimizedImage
        image={posterImage}
        fill
        className="absolute inset-0 -z-10 h-full w-full object-cover"
        sizes="100vw"
      />
      {videoSource && (
        <VideoHeroBackground
          source={videoSource}
          videoSeo={videoSeo}
          shouldLoop={shouldLoop}
          playbackButtonClasses={playbackButtonClasses}
        />
      )}
      {gradientClass && (
        <div
          data-testid="video-hero-overlay"
          className={twJoin('absolute inset-0 -z-10', gradientClass)}
        />
      )}
    </>
  );
}

interface HeroIconsProps {
  icons?: ImageContentFragment | null;
}

function HeroIcons({ icons }: Readonly<HeroIconsProps>) {
  const images = icons?.images;

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto flex w-fit gap-4 sm:gap-6 md:gap-8">
      {images.map((icon) => (
        <OptimizedImage
          key={icon.url}
          image={icon}
          width={100}
          height={50}
          sizes="100px"
          className="h-10 w-auto"
        />
      ))}
    </div>
  );
}

interface HeroContentProps {
  title: RichTextFragment;
  subtitle: RichTextFragment;
  icons?: ImageContentFragment | null;
  cta?: PieceFragment | null;
  textClass: string;
  buttonClasses?: string;
  trackingPrefix?: string;
}

function HeroContent({
  title,
  subtitle,
  icons,
  cta,
  textClass,
  buttonClasses,
  trackingPrefix,
}: Readonly<HeroContentProps>) {
  return (
    <div data-testid="video-hero-content" className={twJoin('max-w-lg', textClass)}>
      <RichTextRenderer blocks={title} className="mb-2 text-3xl sm:mb-4 md:text-4xl lg:text-5xl" />
      <RichTextRenderer blocks={subtitle} className="text-md md:text-lg lg:text-2xl" />
      <HeroIcons icons={icons} />
      <ButtonLink
        ctaComponent={cta}
        className={twJoin(buttonClasses, 'mt-4 w-fit sm:mt-6')}
        trackingPrefix={trackingPrefix}
      />
    </div>
  );
}

export interface VideoHeroProps {
  tile: VideoHeroTile;
  trackingPrefix?: string;
}

// Hook-free itself - the tile is pure markup; only the background player holds client state.
export function VideoHero({ tile, trackingPrefix }: Readonly<VideoHeroProps>) {
  const isFullWidth = tile['full-width']?.value;
  const position = getContentPosition(tile['content-position']?.options?.[0]?.value);
  const { gradientClass, textClass } = getOverlayClasses(
    tile['overlay-style']?.options?.[0]?.value,
    position.gradient,
  );
  const source = resolveVideoSource(tile.video, tile['video-url']?.text);
  // An embed (YouTube, Vimeo) cannot autoplay muted behind the hero, so it stays on the poster.
  const backgroundSource = source && isDirectMediaUrl(source.url) ? source : undefined;
  // The backdrop must never be blank: without an editorial poster the upload's own still steps in,
  // which also covers the cases where the video is not started at all (save-data, embed url, error).
  const posterImage = tile.poster?.images?.[0] ?? source?.thumbnail;

  return (
    <div
      data-testid="video-hero"
      className={getWrapperClasses(isFullWidth, tile['is-not-on-top']?.value)}
    >
      <div
        data-testid="video-hero-frame"
        className={getFrameClasses(isFullWidth, position.contentClasses)}
      >
        <HeroBackdrop
          posterImage={posterImage}
          videoSource={backgroundSource}
          videoSeo={tile['video-seo']}
          shouldLoop={Boolean(tile['replay-video']?.value)}
          gradientClass={gradientClass}
          playbackButtonClasses={position.playbackButtonClasses}
        />
        <HeroContent
          title={tile.title}
          subtitle={tile.subtitle}
          icons={tile.icons}
          cta={tile.cta}
          textClass={textClass}
          buttonClasses={position.buttonClasses}
          trackingPrefix={trackingPrefix}
        />
      </div>
    </div>
  );
}
